import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Input, makeStyles, Theme, Typography } from '@material-ui/core'
import React, {
    FC,
    FocusEventHandler,
    KeyboardEventHandler,
    useCallback,
} from 'react'

import listingColors from 'bpm/pages/ProductsPage/ProductDetailsPage/listingColors'
import clsx from 'clsx'

const { errorColor, premiumPrimary } = listingColors

const useStyles = makeStyles((theme: Theme) => ({
    premPlusInputContainer: {
        position: 'relative',
        display: 'inline-flex',
        '&.error': {
            paddingBottom: theme.spacing(1),
        },
    },
    premPlusInput: {
        display: 'flex',
        boxSizing: 'border-box',
        fontSize: pxToRem(13),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        borderRadius: 100,
        height: pxToRem(23),
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(2),
        outline: 'none',
        border: `1px solid ${premiumPrimary}`,
        '&:hover': {
            cursor: 'pointer',
        },
        '&:focus': {
            outline: `1px solid ${premiumPrimary}`,
        },
        '&.error': {
            border: `1px solid ${errorColor}`,
            outline: 'none',
        },
    },
    isTextOnly: {
        borderRadius: theme.spacing(0.5),
    },
    errorLabel: {
        position: 'absolute',
        color: errorColor,
        height: pxToRem(8),
        fontSize: pxToRem(10),
        left: pxToRem(10),
        top: pxToRem(23),
    },
}))

interface OptionInputProps {
    testId?: string
    error?: string | Record<string, string>
    onBlur?: FocusEventHandler
    defaultValue?: string
    onKeyDown?: KeyboardEventHandler
    onCancel?: () => void
    isTextOnly?: boolean
}

const CHAR_LIMIT = 2000

const OptionInput: FC<OptionInputProps> = ({
    testId,
    defaultValue,
    error,
    isTextOnly,
    onBlur,
    onKeyDown,
    onCancel,
}) => {
    const classes = useStyles()

    const handleKeydown = useCallback(
        (e) => {
            if (e.key === 'Escape') {
                onCancel?.()
            }

            if (e.key === 'Enter' || e.key === 'tab') {
                onBlur?.(e)
                return
            }

            if (e?.target?.value.length <= CHAR_LIMIT) {
                onKeyDown?.(e)
            }
        },
        [onBlur, onKeyDown, onCancel]
    )

    return (
        <Typography
            component="span"
            className={clsx(classes.premPlusInputContainer, { error: !!error })}
        >
            <Input
                className={clsx(classes.premPlusInput, {
                    error: !!error,
                    [classes.isTextOnly]: isTextOnly,
                })}
                type="text"
                onKeyDown={handleKeydown}
                defaultValue={defaultValue}
                onBlur={onBlur}
                required
                disableUnderline
                autoFocus
                inputProps={{
                    maxLength: 2000,
                    'data-testid': testId ?? 'optionInput',
                }}
            ></Input>
            {!!error && (
                <Typography component="span" className={classes.errorLabel}>
                    {error}
                </Typography>
            )}
        </Typography>
    )
}

export default OptionInput
